import * as React from "react";

// MUI
import { ThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

// Components
import StyledProgressStepper from "./components/common/ProgressStepper.Component";

// Utility
import theme from "./domain/utility/theme";

// Sections
import StyledSelectAppointmentTypeSection from "./sections/SelectAppointmentType.Section";
import { AnimatedPatientAttributesQuestionaireSection } from "./sections/PatientAttributesQuestionaire.Section";
import StyledAppointmentTimeSelectionSection from "./sections/AppointmentTimeSelection.Section";
import StyledPersonalDetailsInputSectionV2 from "./sections/PersonalDetailsInputV2.Section";
import StyledHealthCoverDetailsSection from "./sections/HealthCoverDetails.Section";
import StyledVerifyPasscodeSection from "./sections/VerifyPassCode.Section";
import { AnimatedTermsAndConditionsSection } from "./sections/TermsAndConditions.Section";
import { AnimatedPrepaymentSection } from "./sections/Prepayment.Section";
import { AnimatedBookingConfirmedSection } from "./sections/BookingConfirmed.Section";

// Enums
import JourneyStage from "./domain/enums/JourneyStage";

// Classes
import AppointmentBookingRequest from "./domain/classes/AppointmentBookingRequest";
import Utility from "./domain/classes/Utility";

// Stripe
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

// Config
import APPLICATION_VERSION, {
	STRIPE_PK_PROD,
	STRIPE_PK_TEST,
} from "./domain/utility/config";

// NPM
import { useCookies } from "react-cookie";

// Contexts
import { useApplicationManager } from "./contexts/ApplicationManager.Context";
import QuestionaireManager from "./contexts/QuestionaireManager.Context";
import CalendarManager from "./contexts/CalendarManager.Context";
import DoctorsManager from "./contexts/DoctorsManager.Context";
import PaymentManager from "./contexts/PaymentManager.Context";

// Assets

import SavedBookingDialog from "./components/common/SavedBookingDialog.Component";

export interface AppProps {
	wrapper: HTMLElement | null;
}

const App: React.FC<AppProps> = ({ wrapper }) => {
	const [cookies] = useCookies();

	const {
		handleLocationChange,
		journeyStage,
		appointmentBookingRequest,
		handleAppointmentBookingRequestChange,
		handleSelectedDoctorChange,
		handleSelectedApptTypeChange,
		handleJourneyStageChange,
		handleStageResponseChange,
		clearApplicationData,
		clearCookies,
		dialog,
	} = useApplicationManager();

	// States
	const [retrievedStage, setRetrievedStage] = React.useState<number>(0);

	// Callbacks
	const resumeBookingAsync = React.useCallback(async () => {
		const stageId = cookies["stage_id"];
		// Utility.debug("StageId: " + stageId);

		if (stageId) {
			const result = await AppointmentBookingRequest.resumeBooking(
				stageId
			);
			// Utility.debug(result.data);
			handleSelectedDoctorChange(cookies["doctor"]);
			handleSelectedApptTypeChange(cookies["appointment_type"]);
			handleStageResponseChange({
				...result.data,
				clientSecret: cookies["client_secret"],
			});

			setRetrievedStage(result.data.stage);
		}
	}, []);

	// Effects
	React.useEffect(() => {
		resumeBookingAsync();
	}, [resumeBookingAsync]);

	// React.useEffect(() => {
	// 	console.log("Application version:", APPLICATION_VERSION);
	// }, []);

	React.useEffect(() => {
		const organisationId: number = parseInt(
			(wrapper as HTMLDivElement).getAttribute(
				"organisation-id"
			) as string
		);

		const branchId: number = parseInt(
			(wrapper as HTMLDivElement).getAttribute("branch-id") as string
		);

		handleAppointmentBookingRequestChange(
			AppointmentBookingRequest.parseLocationDetails(
				{ organisationId, branchId },
				appointmentBookingRequest
			)
		);

		// handleLocationChange({ branchId, organisationId });
	}, [wrapper, handleAppointmentBookingRequestChange]);

	React.useEffect(()=>{
		console.log('wrapper changed!');
		const organisationId: number = parseInt(
			(wrapper as HTMLDivElement).getAttribute(
				"organisation-id"
			) as string
		);

		const branchId: number = parseInt(
			(wrapper as HTMLDivElement).getAttribute("branch-id") as string
		);
		handleLocationChange({ organisationId, branchId });

	}, [wrapper, handleLocationChange])

	// React.useEffect(() => {
	// 	getAllAppointmentTypes(location.organisationId);
	// }, [location, getAllAppointmentTypes]);

	const [stripePromise, setStripePromise] = React.useState<Stripe | null>(
		null
	);

	// Callbacks
	const loadStripeAsync = React.useCallback(async () => {
		const pk =
			process.env.NODE_ENV === "production"
				? STRIPE_PK_PROD
				: STRIPE_PK_TEST;

		let attempts = 0;
		let result = null;
		do {
			result = await loadStripe(pk);
		} while (result === null && attempts < 2);

		setStripePromise(result);
	}, []);

	// Effects
	React.useEffect(() => {
		loadStripeAsync();
	}, []);

	// handler
	const handleCancel = () => {
		clearCookies();
		clearApplicationData();
		setRetrievedStage(0);
	};

	const handleContinue = () => {
		if (retrievedStage === 2) {
			handleJourneyStageChange(JourneyStage.VerifyPasscodeInput);
		} else if (retrievedStage === 3) {
			handleJourneyStageChange(JourneyStage.TermsAndConditions);
		}
		setRetrievedStage(0);
	};

	return (
		<ThemeProvider theme={theme}>
			<div className="app">
				<CssBaseline />
				<StyledProgressStepper />
				<div style={{ height: 48 }} />
				{journeyStage === JourneyStage.AppointmentTypeSelection && (
					<StyledSelectAppointmentTypeSection />
				)}
				{journeyStage ===
					JourneyStage.PatientAttributesQuestionaire && (
					<QuestionaireManager>
						<AnimatedPatientAttributesQuestionaireSection />
					</QuestionaireManager>
				)}
				{journeyStage === JourneyStage.AppointmentTimeSelection && (
					<CalendarManager>
						<DoctorsManager>
							<StyledAppointmentTimeSelectionSection />
						</DoctorsManager>
					</CalendarManager>
				)}
				{journeyStage === JourneyStage.PersonalDetailsInput && (
					<StyledPersonalDetailsInputSectionV2 />
				)}
				{journeyStage === JourneyStage.HealthCoverDetailsInput && (
					<StyledHealthCoverDetailsSection />
				)}
				{journeyStage === JourneyStage.VerifyPasscodeInput && (
					<StyledVerifyPasscodeSection />
				)}
				{journeyStage === JourneyStage.TermsAndConditions && (
					<AnimatedTermsAndConditionsSection />
				)}
				{journeyStage === JourneyStage.AppointmentPrepayment && (
					<Elements stripe={stripePromise}>
						<PaymentManager>
							<AnimatedPrepaymentSection />
						</PaymentManager>
					</Elements>
				)}
				{journeyStage === JourneyStage.BookingConfirmed && (
					<AnimatedBookingConfirmedSection />
				)}
			</div>
			{dialog}
			<SavedBookingDialog
				onCancel={handleCancel}
				onContinue={handleContinue}
				open={retrievedStage !== 0}
			/>
		</ThemeProvider>
	);
};

export default App;
